import I18n from 'I18n';
import { stringify } from 'hub-http/helpers/params';
import * as OfferingType from 'marketplace-ui-common/offerings/constants/OfferingType';
import { TYPEAHEAD_SEARCH_RESULTS_LIMIT } from 'marketplace-ui-common/constants/search';
import { useCallback } from 'react';
import { getMarketplaceSearchClient } from '../clients/marketplaceSearchClient';
import { useAssetSearch } from './useAssetSearch';
import { getModuleMarketplaceUrl, getTemplateMarketplaceUrl, getThemeMarketplaceUrl } from '../utils/urls';
import AssetSearchBarResult from '../components/storefront/search/components/AssetSearchBarResult';
import { jsx as _jsx } from "react/jsx-runtime";
export const VIEW_ALL_THEMES = '__VIEW_ALL_THEMES__';
export const VIEW_ALL_MODULES = '__VIEW_ALL_MODULES__';
const VIEW_ALL_ASSETS = '__VIEW_ALL_ASSETS__';
const SEARCH_URL = 'assets/search';
export function useAssetSearchBarQuery({
  appContext = 'app',
  grouped = false,
  limit = TYPEAHEAD_SEARCH_RESULTS_LIMIT,
  offeringType = 'THEME'
} = {}) {
  const {
    fetchAssets,
    useChirp
  } = useAssetSearch(appContext);
  const offeringTypeIsModule = offeringType === OfferingType.MODULE || Array.isArray(offeringType) && offeringType.length === 1 && offeringType.includes('MODULE');
  const searchBarGroupLabelMessage = I18n.text('marketplace-assets-ui.storefront.search.offeringsSearchBarGroupLabel');
  const getAssetDetailsUrl = useCallback((_offeringType, pathname) => {
    return _offeringType === 'MODULE' ? getModuleMarketplaceUrl(appContext, pathname) : getThemeMarketplaceUrl(appContext, pathname);
  }, [appContext]);
  const getViewAllUrl = useCallback(input => {
    const query = stringify({
      eco_search: input
    });
    return getTemplateMarketplaceUrl(appContext, `search?${query}`);
  }, [appContext]);
  const getSearchBarViewAllLabelMessage = useCallback(total => {
    if (offeringTypeIsModule) {
      return I18n.text('marketplace-assets-ui.storefront.search.moduleSearchBarViewAll', {
        total
      });
    }
    return I18n.text('marketplace-assets-ui.storefront.search.offeringsSearchBarViewAll', {
      total
    });
  }, [offeringTypeIsModule]);
  const fetcher = useCallback(input => {
    const offeringTypeValues = offeringType ? Array.isArray(offeringType) ? offeringType : [offeringType] : ['MODULE', 'THEME'];
    const offeringTypesFilter = {
      values: offeringTypeValues,
      clause: 'OR'
    };
    let response;
    if (useChirp) {
      response = fetchAssets({
        limit,
        search: input,
        offeringTypes: offeringTypesFilter
      }).then(([resp]) => resp);
    } else {
      response = getMarketplaceSearchClient(appContext).post(SEARCH_URL, {
        data: {
          limit,
          search: input,
          offeringType: offeringTypesFilter
        }
      });
    }
    return response.then(({
      items,
      total
    }) => {
      const options = items.map(item => {
        const {
          companyName,
          name,
          offeringId,
          offeringType: _offeringType,
          slug,
          thumbnailUrl
        } = item;
        const offering = _offeringType === 'MODULE' ? 'module' : 'theme';
        return {
          dropdownText: /*#__PURE__*/_jsx(AssetSearchBarResult, {
            companyName: companyName,
            name: name,
            offeringType: _offeringType,
            thumbnailUrl: thumbnailUrl || ''
          }),
          href: getAssetDetailsUrl(_offeringType, slug),
          item,
          linkUse: 'unstyled',
          text: name,
          value: `${offering}::${offeringId}`,
          slug
        };
      });
      if (total > limit) {
        options.push({
          href: getViewAllUrl(input),
          text: getSearchBarViewAllLabelMessage(total),
          value: VIEW_ALL_ASSETS,
          slug: `search?${stringify({
            eco_search: input
          })}`
        });
      }
      return options;
    }).then(options => grouped ? [{
      text: searchBarGroupLabelMessage,
      options
    }] : options).catch(error => {
      console.error('Error fetching asset search results:', error);
      throw error;
    });
  }, [appContext, fetchAssets, getAssetDetailsUrl, getSearchBarViewAllLabelMessage, getViewAllUrl, grouped, limit, offeringType, searchBarGroupLabelMessage, useChirp]);
  return fetcher;
}